import React from 'react';
import PropTypes from 'prop-types';
import { usePrefs } from 'wsm-common-data';
import { useIsMobile } from '../../hooks/useIsMobile';
import Details from './Details';
import Image from './Image';
import ContactActions from './ContactActions';
import { StaffMemberShape } from '../../global-constants';

// This component exists to render the staff member's details and contact actions within the modal.
// Previously, this was accomplished by reusing the VCard component, but this approach
// meant we had a single more complicated component, and making changes to it
// required extensive regression testing for both vertical cards and modal cards even if we only wanted to update one of these two configurations.

// This new approach means there is duplication between VCard, HCard and ModalCard, but it is a tradeoff we are willing to make
// to facilitate development.
// This may change in the future, as we work towards a more stable Staff page design
// and gain a better understanding of the needs of dealerships when using this page.
const ModalCard = ({ staffMember }) => {
	const { imageShape, squareImagePadding, verticalImageBackgroundColor } =
		usePrefs();
	const { name, photo, department } = staffMember;
	const isCircleImg = imageShape === 'circle';

	const { isMobile } = useIsMobile();

	const getDetailsContainerClasses = () => {
		const containerCls = ['px-5', 'pt-5', 'pb-6', 'w-100'];

		// Prevents doubling the amount of empty white space between images and the details container
		// for specific pref combinations
		if (
			(squareImagePadding === 'true' && !isCircleImg) ||
			(isCircleImg && verticalImageBackgroundColor !== 'bg-transparent')
		) {
			containerCls.push('pt-5');
		}

		return containerCls.join(' ');
	};

	return (
		<div
			className={`${
				isMobile
					? 'd-flex flex-column align-items-center'
					: 'd-flex justify-content-center'
			}`}
			data-testid="ModalCard"
		>
			{/* 
				We use specific classes to target elements within the modal because a Boostrap modal effectively lives "outside" of the widget's 
				CSS structure, i.e.: even if we render the Boostrap modal within a div with a specific class, 
				the modal itself will not inherit the styles applied to that class nor to its children.
			*/}
			<div
				className="ws-stf-lstng-modal-img-container"
				data-testid="modalCardImgContainer"
			>
				<Image
					name={name}
					department={department}
					url={photo}
					delayLoadImage={false}
					isWithinModal
				/>
			</div>
			<div
				className={getDetailsContainerClasses()}
				style={{ minHeight: `${isMobile ? '20rem' : '14rem'}` }}
			>
				<Details staffMember={staffMember} isWithinModal />
				<ContactActions staffMember={staffMember} isWithinModal />
			</div>
		</div>
	);
};

ModalCard.propTypes = {
	staffMember: PropTypes.shape(StaffMemberShape).isRequired
};

export default ModalCard;
